// Reads the download filename back out of a Content-Disposition header.
//
// The API sends both forms (see apps/api/src/http/contentDisposition.ts):
//
//   attachment; filename="Biologie-notes.pdf"; filename*=UTF-8''Biologi%C3%AB-notes.pdf
//
// The plain `filename` is a deliberately lossy ASCII fallback — diacritics
// folded, anything without an ASCII equivalent collapsed to "_" — because Node
// cannot put a code point above U+00FF in a header value at all. So `filename*`
// is the real name and must win wherever it parses; reading only the fallback
// would silently rename every download in a language this app is translated
// into.

/** RFC 5987 ext-value: charset'language'percent-encoded-value. */
const EXTENDED = /filename\*\s*=\s*([^']*)'([^']*)'([^;]+)/i;
const QUOTED = /filename\s*=\s*"([^"]*)"/i;
const UNQUOTED = /filename\s*=\s*([^;]+)/i;

function decodeExtended(charset: string, encoded: string): string | null {
  const raw = encoded.trim();
  if (!raw) return null;

  try {
    const normalised = charset.trim().toLowerCase();
    if (normalised === "iso-8859-1") {
      // Percent-escapes are Latin-1 bytes here, not UTF-8 sequences.
      return unescape(raw.replace(/%(?![0-9a-f]{2})/gi, "%25"));
    }
    // UTF-8, and anything unlabelled — decodeURIComponent assumes UTF-8.
    return decodeURIComponent(raw);
  } catch {
    // Malformed percent-encoding: fall back rather than throwing mid-download.
    return null;
  }
}

/** C0 controls and DEL, which a `download` attribute preserves verbatim. */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001f\u007f]/g;

/**
 * Mirrors the server's own filename policy (apps/api/src/http/contentDisposition.ts
 * and `safeFileStem`): no path separators, no leading dots, no control
 * characters. The server sanitises already, but percent-decoding happens on
 * this side, so `filename*=UTF-8''safe%00name.pdf` reconstitutes a NUL here
 * that no server-side check ever saw — and this value goes straight into a
 * download attribute, so it is not taken on trust.
 */
function sanitiseFilename(filename: string): string {
  return filename
    .replace(CONTROL_CHARS, "")
    .replace(/[\\/]/g, "_")
    .replace(/^\.+/, "")
    .trim();
}

export function filenameFromDisposition(disposition: string | null | undefined): string | null {
  if (!disposition) return null;

  const extended = disposition.match(EXTENDED);
  if (extended) {
    const decoded = decodeExtended(extended[1], extended[3]);
    const cleaned = decoded && sanitiseFilename(decoded);
    if (cleaned) return cleaned;
  }

  const quoted = disposition.match(QUOTED);
  if (quoted) {
    const cleaned = sanitiseFilename(quoted[1]);
    if (cleaned) return cleaned;
  }

  // `filename=plain.pdf` — legal, and what a hand-rolled header often sends.
  // Matched last so it cannot swallow the `filename*` parameter's own value.
  const unquoted = disposition.replace(EXTENDED, "").match(UNQUOTED);
  if (unquoted) {
    const cleaned = sanitiseFilename(unquoted[1].trim());
    if (cleaned) return cleaned;
  }

  return null;
}
